<script setup lang="ts">
import { computed, ref, watch } from "vue";

const emit = defineEmits([
  "toggleActive",
  "toggleClassicTrekNavActive",
  "toggleWorldwideTrekNavActive",
]);
const route = useRoute();
const site = useSite();
const { gsap } = useGSAP();
const { menuTreks, ensureMenuTreks } = useMenuTreks();

interface Child {
  isListed: boolean;
  template: string;
  trekLocation: string;
  id?: string | number;
  uri?: string;
  title?: string;
  trekDistance?: string;
  trekElevation?: string;
  trekGrade?: string;
  coverImage?: { url?: string; alt?: string } | null;
  trekNavCoverImage?: { url?: string; alt?: string } | null;
}

const delayTime = 300;
const listedChildren = computed(
  () =>
    site.value?.children?.filter(
      (child: Child) => child.isListed && child.template !== "treks",
    ) ?? [],
);

function treksForLocation(location: "Europe" | "Worldwide"): Child[] {
  const cards = (menuTreks.value ?? []).filter(
    (trek) =>
      trek?.isListed &&
      trek?.template === "treks" &&
      trek?.trekLocation === location,
  ) as Child[];
  if (cards.length) return cards;

  return (
    site.value?.children?.filter(
      (child: Child) =>
        child.isListed &&
        child.template === "treks" &&
        child.trekLocation === location,
    ) ?? []
  );
}

const classicTreks = computed(() => treksForLocation("Europe"));
const worldwideTreks = computed(() => treksForLocation("Worldwide"));

// const ourTreksChildren = computed(() => {
//   //   console.log("site.value:", site.value); // Debug log
//   if (!site.value || !site.value.children) {
//     return []; // Handle undefined or empty site
//   }
//   return site.value.children.filter(
//     (child: Child) => child.isListed && child.template === "treks",
//   );
// });

const isActive = ref(false);
const isTrekNavActive = ref(false);
const isWorldwideTrekNavActive = ref(false);
const menuItem = ref();
const navText = ref();

const toggleActive = () => {
  isActive.value = !isActive.value;
  emit("toggleActive");
};

let showTimeout: NodeJS.Timeout | undefined;
const toggleClassicTrekNavActive = () => {
  clearTimeout(showTimeout);
  void ensureMenuTreks();
  showTimeout = setTimeout(() => {
    isTrekNavActive.value = !isTrekNavActive.value;
    isActive.value = false;
  }, delayTime);

  emit("toggleClassicTrekNavActive");
};

const toggleWorldwideTrekNavActive = () => {
  clearTimeout(showTimeout);
  void ensureMenuTreks();
  showTimeout = setTimeout(() => {
    isWorldwideTrekNavActive.value = !isWorldwideTrekNavActive.value;
    isActive.value = false;
  }, delayTime);

  emit("toggleWorldwideTrekNavActive");
};

watch(isActive, (newValue) => {
  if (newValue && gsap.value) {
    // Trigger the animation when the menu becomes active
    if (menuItem.value) {
      gsap.value.from(menuItem.value, {
        delay: 0.5,
        duration: 0.4,
        x: -50,
        opacity: 0,
        stagger: 0.08,
      });
    }
    if (navText.value) {
      gsap.value.from(navText.value, {
        delay: 1.2,
        duration: 0.7,
        x: -100,
        opacity: 0,
      });
    }
  }
});
</script>

<template>
  <div id="sideMenu">
    <button
      aria-label="Menu"
      class="sidemenu__btn"
      :class="{ active: isActive }"
      @click="toggleActive"
    >
      <span class="open-nav"
        ><svg viewBox="0 0 40 30" xmlns="http://www.w3.org/2000/svg">
          <!-- Top Line -->
          <rect y="0" width="40" height="1.5" fill="white" />
          <!-- Middle Line -->
          <rect y="10" width="40" height="1.5" fill="white" />
          <!-- Bottom Line -->
          <rect y="20" width="40" height="1.5" fill="white" />
        </svg>
      </span>
      <span class="close-nav"
        ><svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <line x1="10" y1="10" x2="30" y2="30" stroke-width="4" />
          <line x1="10" y1="30" x2="30" y2="10" stroke-width="4" /></svg
      ></span>
    </button>

    <Transition name="translateX" mode="out-in">
      <div v-if="isTrekNavActive" class="treks-nav-mobile">
        <div
          class="close-trek-nav"
          :class="{ active: isTrekNavActive }"
          @click="toggleClassicTrekNavActive"
        >
          <span class="close-nav"
            ><svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
              <line x1="10" y1="10" x2="30" y2="30" stroke-width="4" />
              <line x1="10" y1="30" x2="30" y2="10" stroke-width="4" /></svg
          ></span>
        </div>
        <div class="treks-nav-wrap">
          <div v-for="trek in classicTreks" :key="trek.id" class="trek">
            <NuxtLink
              class="btn from-left"
              :to="{ path: `/${trek.uri}` }"
              @click="toggleClassicTrekNavActive"
            >
              <div v-if="trek?.trekDistance" class="trek-stats">
                <p v-if="trek?.trekDistance" class="trek-distance">
                  {{ trek?.trekDistance }}
                </p>
                <p v-if="trek?.trekElevation" class="trek-elevation">
                  {{ trek?.trekElevation }}
                </p>
              </div>
              <div class="trek-image">
                <NuxtPicture
                  v-if="trek?.coverImage"
                  loading="lazy"
                  :src="trek?.trekNavCoverImage.url"
                  :width="400"
                  :height="250"
                  :alt="trek?.trekNavCoverImage.alt"
                  sizes="sm:200px md:300px lg:400px"
                />
              </div>
              <div class="trek-title">
                <h3>{{ trek?.title }}</h3>
                <div v-if="trek?.trekGrade === 'Grade 1'" class="trek-grade">
                  <AppSVGGradeEasy />
                  <p>Easy</p>
                </div>
                <div v-if="trek?.trekGrade === 'Grade 2'" class="trek-grade">
                  <AppSVGGradeModerate />
                  <p>Moderate</p>
                </div>
                <div v-if="trek?.trekGrade === 'Grade 3'" class="trek-grade">
                  <AppSVGGradeDifficult />
                  <p>Difficult</p>
                </div>
                <div v-if="trek?.trekGrade === 'Grade 4'" class="trek-grade">
                  <AppSVGGradeToughest />
                  <p>Toughest</p>
                </div>
              </div>
            </NuxtLink>
          </div>
          <div
            class="trek worldwide-treks-text"
            v-html="site?.comingSoonTrek"
          />
        </div>
      </div>
    </Transition>

    <Transition name="translateX" mode="out-in">
      <div v-if="isWorldwideTrekNavActive" class="treks-nav-mobile worldwide">
        <div
          class="close-trek-nav"
          :class="{ active: isWorldwideTrekNavActive }"
          @click="toggleWorldwideTrekNavActive"
        >
          <span class="close-nav"
            ><svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
              <line x1="10" y1="10" x2="30" y2="30" stroke-width="4" />
              <line x1="10" y1="30" x2="30" y2="10" stroke-width="4" /></svg
          ></span>
        </div>
        <div class="treks-nav-wrap">
          <div v-for="trek in worldwideTreks" :key="trek.id" class="trek">
            <NuxtLink
              class="btn from-left"
              :to="{ path: `/${trek.uri}` }"
              @click="toggleWorldwideTrekNavActive"
            >
              <div v-if="trek?.trekDistance" class="trek-stats">
                <p v-if="trek?.trekDistance" class="trek-distance">
                  {{ trek?.trekDistance }}
                </p>
                <p v-if="trek?.trekElevation" class="trek-elevation">
                  {{ trek?.trekElevation }}
                </p>
              </div>
              <div class="trek-image">
                <NuxtPicture
                  v-if="trek?.coverImage"
                  loading="lazy"
                  :src="trek?.trekNavCoverImage.url"
                  :width="400"
                  :height="250"
                  :alt="trek?.trekNavCoverImage.alt"
                  sizes="sm:200px md:300px lg:400px"
                />
              </div>
              <div class="trek-title">
                <h3>{{ trek?.title }}</h3>
                <div v-if="trek?.trekGrade === 'Grade 1'" class="trek-grade">
                  <AppSVGGradeEasy />
                  <p>Easy</p>
                </div>
                <div v-if="trek?.trekGrade === 'Grade 2'" class="trek-grade">
                  <AppSVGGradeModerate />
                  <p>Moderate</p>
                </div>
                <div v-if="trek?.trekGrade === 'Grade 3'" class="trek-grade">
                  <AppSVGGradeDifficult />
                  <p>Difficult</p>
                </div>
                <div v-if="trek?.trekGrade === 'Grade 4'" class="trek-grade">
                  <AppSVGGradeToughest />
                  <p>Toughest</p>
                </div>
              </div>
            </NuxtLink>
          </div>
          <div
            class="trek worldwide-treks-text"
            v-html="site?.comingSoonTrek"
          />
        </div>
      </div>
    </Transition>

    <Transition name="translateX">
      <div
        v-if="isActive"
        class="sidemenu__wrapper"
        :class="{ active: isActive }"
      >
        <nav>
          <TransitionGroup class="main-menu" name="list" tag="ul" :css="false">
            <li key="our-treks" ref="menuItem" class="menu-item our-treks">
              <NuxtLink @click.prevent="toggleClassicTrekNavActive">
                Classic Treks
              </NuxtLink>
            </li>
            <li key="our-treks" ref="menuItem" class="menu-item our-treks">
              <NuxtLink @click.prevent="toggleWorldwideTrekNavActive">
                Worldwide Treks
              </NuxtLink>
            </li>
            <li
              v-for="(item, index) in listedChildren"
              :key="index"
              ref="menuItem"
              class="menu-item"
              :class="{ menuactive: isActive }"
            >
              <NuxtLink
                :to="{ path: `/${item.uri}` }"
                :aria-current="
                  route.path.startsWith(`/${item.id}`) ? 'page' : undefined
                "
                @click="toggleActive"
              >
                {{ item.title }}
              </NuxtLink>
            </li>
          </TransitionGroup>
        </nav>
      </div>
    </Transition>
  </div>
</template>
